import {
  useGetSubscription,
  useListBillingPlans,
  useListPaymentMethods,
  useUpdateSubscription,
} from "@/api/stripe";
import { NESTED_DIALOG_Z } from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { ROUTES } from "@/constants/routes";
import { PlanConfigurationModal } from "@/pages/billing/PlanConfigurationModal";
import {
  buildPlanChangeAction,
  resolvePlanToConfigure,
  transformBillingPlans,
  type PlanSelection,
} from "@/utils/planChange";
import { route } from "@/utils/route";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

/**
 * Managing the plan — seats and billing interval on the plan you are already
 * on, and what happens when it resolves.
 *
 * There is one plan, so there is nothing to choose between: "Manage plan"
 * opens its configuration directly. An in-place subscription update when the
 * plan allows it, otherwise Stripe checkout, which returns to `redirectTo`.
 */
export function useManagePlan(redirectTo: string = ROUTES.SETTINGS.USAGE) {
  const navigate = useNavigate();
  const { data: subscription, isPending: isSubscriptionPending } =
    useGetSubscription();
  const { data: billingPlans } = useListBillingPlans();
  const { data: paymentMethods } = useListPaymentMethods();
  const updateSubscription = useUpdateSubscription();

  const [isOpen, setIsOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);

  const plans = useMemo(
    () => transformBillingPlans(billingPlans?.data.billing_plans),
    [billingPlans],
  );

  // Derived rather than captured on open: a deep link fires before the plan
  // list has loaded, and snapshotting at click time opened nothing at all.
  //
  // Held back until the subscription resolves, too. The plan list usually wins
  // that race, and mounting on it seeded the modal with plans[0] and zero
  // seats — an "Update plan" from there wrote away the seats the org had paid
  // for. A subscription that resolves to no plan of ours falls back to Pro —
  // see resolvePlanToConfigure.
  const currentPlan = useMemo(() => {
    if (isSubscriptionPending) return null;
    return resolvePlanToConfigure(
      plans,
      subscription?.data.stripe_subscription?.plan_name,
    );
  }, [plans, subscription, isSubscriptionPending]);

  // Both stable: the shell's deep-link effect keys on the opener's identity and
  // its `closeSettings` closes over the closer.
  const openManagePlan = useCallback(() => {
    setOpenCount((count) => count + 1);
    setIsOpen(true);
  }, []);
  const closeManagePlan = useCallback(() => setIsOpen(false), []);

  const handleComplete = async (selection: PlanSelection) => {
    setIsOpen(false);

    const planChange = buildPlanChangeAction({
      selection,
      subscription: subscription?.data.stripe_subscription,
      paymentMethods: paymentMethods?.data.payment_methods,
    });

    if (planChange.type === "update") {
      try {
        const updated = await updateSubscription.mutateAsync(
          planChange.payload,
        );
        const scheduledPlan =
          updated.data.stripe_subscription.scheduled_plan_display_name;
        toast.success(
          scheduledPlan
            ? `Switch to ${scheduledPlan} scheduled`
            : "Plan updated successfully",
        );
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to update plan",
        );
      }
      return;
    }

    navigate(
      route(
        ROUTES.BILLING.CHECKOUT,
        {},
        {
          plan: selection.plan.name,
          billing_cycle: selection.billing_cycle,
          user_count: String(selection.user_count),
          redirect_to: redirectTo,
        },
      ),
    );
  };

  /** Mount this wherever the trigger lives; it renders nothing until opened. */
  const managePlan =
    openCount > 0 && currentPlan ? (
      <PlanConfigurationModal
        // The modal seeds seats and interval on mount and stays mounted
        // through a close, so a fresh key per open discards a half-made
        // selection instead of reopening on it.
        key={openCount}
        plan={currentPlan}
        open={isOpen}
        buttonLoading={updateSubscription.isPending}
        redirectTo={redirectTo}
        // Explicit rather than inherited from sibling mount order: this modal
        // always opens over Settings.
        zIndexClassName={NESTED_DIALOG_Z}
        onComplete={handleComplete}
        onClose={closeManagePlan}
      />
    ) : null;

  return { openManagePlan, closeManagePlan, managePlan };
}
